/**
 * Mobile Device Detection Hook
 * Provides reactive screen size detection for responsive rendering.
 * SSR-safe with graceful fallback to desktop.
 */
import { useState, useEffect, useCallback } from 'react';

/** Breakpoint thresholds in pixels */
const BREAKPOINTS = {
    mobile: 768,
    tablet: 1024,
    desktop: 1280,
} as const;

/** Device type based on screen width */
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/** Hook return type */
export interface DeviceDetectionResult {
    /** Current device type based on screen width */
    deviceType: DeviceType;
    /** True if screen width < 768px */
    isMobile: boolean;
    /** True if screen width >= 768px and < 1024px */
    isTablet: boolean;
    /** True if screen width >= 1024px */
    isDesktop: boolean;
    /** Current screen width in pixels */
    screenWidth: number;
    /** Current screen height in pixels */
    screenHeight: number;
    /** True if device is in portrait orientation */
    isPortrait: boolean;
    /** True if device supports touch input */
    isTouchDevice: boolean;
}

/**
 * Determines device type from screen width
 */
function getDeviceType(width: number): DeviceType {
    if (width < BREAKPOINTS.mobile) return 'mobile';
    if (width < BREAKPOINTS.tablet) return 'tablet';
    return 'desktop';
}

/**
 * Checks if touch is supported
 */
function checkTouchSupport(): boolean {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Gets initial dimensions (SSR-safe)
 */
function getInitialDimensions(): { width: number; height: number } {
    if (typeof window === 'undefined') {
        return { width: 1280, height: 800 }; // Default to desktop for SSR
    }
    return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Hook for detecting device type and screen dimensions.
 * Updates reactively on window resize.
 * 
 * @example
 * ```tsx
 * const { isMobile, deviceType } = useDeviceDetection();
 * 
 * if (isMobile) {
 *   return <MobileLayout />;
 * }
 * return <DesktopLayout />;
 * ```
 */
export function useDeviceDetection(): DeviceDetectionResult {
    const [dimensions, setDimensions] = useState(getInitialDimensions);
    const [isTouchDevice, setIsTouchDevice] = useState(false);

    const handleResize = useCallback(() => {
        setDimensions({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    }, []);

    useEffect(() => {
        // Set initial values on client
        setDimensions({
            width: window.innerWidth,
            height: window.innerHeight,
        });
        setIsTouchDevice(checkTouchSupport());

        // Debounced resize handler
        let timeoutId: ReturnType<typeof setTimeout>;
        const debouncedResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleResize, 100);
        };

        window.addEventListener('resize', debouncedResize);
        return () => {
            window.removeEventListener('resize', debouncedResize);
            clearTimeout(timeoutId);
        };
    }, [handleResize]);

    const deviceType = getDeviceType(dimensions.width);

    return {
        deviceType,
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop' || deviceType === 'tablet',
        screenWidth: dimensions.width,
        screenHeight: dimensions.height,
        isPortrait: dimensions.height > dimensions.width,
        isTouchDevice,
    };
}

export { BREAKPOINTS };
